import React, { useEffect } from 'react';
import { User } from '../../../global';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import axiosHelper from '../../../helpers/axiosHelper';
import CountrySelect from '../../Forms/Selects/CountrySelect';
import BirthdateEditor from './BirthdateEditor';
import { profilePreviewUrlForUser } from '../../../helpers/navigationUtils';
import ErrorBlock from '../../Forms/ErrorBlock';
import FormField from '../../Forms/FormField';
import { useBanner } from '../../../context/BannerContext';
interface Props {
  user: User;
  onChange: (user: User) => void;
}

const YourProfile: React.FC<Props> = ({ user, onChange }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = useForm<Partial<User>>({
    defaultValues: user,
  });
  const { createAndAddBannerMessage } = useBanner();
  useEffect(() => {
    reset(user);
  }, [user, reset]);

  const onSubmit: SubmitHandler<Partial<User>> = async (data) => {
    console.log(data);
    const userdata = await axiosHelper.updateDSLUser(data);
    if (userdata) {
      createAndAddBannerMessage('Profile updated successfully!', 'success', 'profile-update-success');
      onChange(userdata);
    }
  };
  const gotoProfilePreview: SubmitHandler<Partial<User>> = (data) => {
    window.open(profilePreviewUrlForUser(user), 'blank');
  };
  const containsOnlyNumbers = async (str?: string) => {
    console.log('validating number', str);
    if (str) {
      if (/^\d+$/.test(str)) return true;
      else return 'Mobile number must contain only numbers';
    }
    return true;
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="px-5 mt-7">
        <div className="row mb-7 mt-4">
          <div className="col-12 col-md-2 text fw-bold"></div>
          <div className="col-12 col-md-10">
            <span className="text color-blue">
              <i>
                <b>
                  <span className="form-required">*</span> &nbsp; Mandatory fields required to {user?.userType === 'D' ? 'upload your design' : 'launch a contest'}
                </b>
              </i>
            </span>
          </div>
        </div>

        <FormField name="username" label="Username">
          <input type="text" style={{ border: '0px ' }} id="username" disabled={true} {...register('username')} />
        </FormField>

        <FormField name="email" label="Email">
          <input type="text" style={{ border: '0px ' }} id="email" disabled={true} {...register('email')} />
        </FormField>

        <FormField name="firstName" label="First name" required error={errors.firstName}>
          <input type="text" {...register('firstName', { required: 'First name is required' })} />
        </FormField>

        <FormField name="lastName" label="Last name" required error={errors.lastName}>
          <input type="text" {...register('lastName', { required: 'Last name is required' })} />
        </FormField>

        <FormField name="hasPublicName">
          <input type="checkbox" {...register('hasPublicName')} tabIndex={0} />
          <label>Public (your full name visible in your profile page)</label>
        </FormField>
        {user?.userType === 'D' && (
          <>
            <FormField name="biography" label="Biography" error={errors.biography}>
              <textarea placeholder={'Please write here a short bio'} rows={7} className="d-flex flex-col justify-content-center align-items-center " {...register('biography')} />
            </FormField>

            <FormField name="birthDate" label="Birth date" required error={errors.birthDate}>
              <Controller
                name="birthDate"
                control={control}
                rules={{ required: 'Birthdate is required' }} // Add required validation rule
                render={({ field: { value, onChange }, fieldState: { error } }) => <BirthdateEditor value={value || ''} onChange={onChange} />}
              />
            </FormField>

            <FormField name="hasPublicBirthDate">
              <input type="checkbox" className="" {...register('hasPublicBirthDate')} tabIndex={0} />
              <label>Public (birth year visible in your profile page)</label>
            </FormField>

            <FormField name="gender" label="Gender" required error={errors.gender}>
              <div className="col-12 d-flex flex-row justify-content-between align-items-center ">
                <div>
                  <input value="N" type="radio" {...register('gender', { required: 'Gender is required' })} tabIndex={0} />
                  <label>N/A</label>
                </div>
                <div>
                  <input type="radio" value="M" {...register('gender', { required: 'Gender is required' })} tabIndex={0} />
                  <label>Male</label>
                </div>
                <div>
                  <input type="radio" value="F" {...register('gender', { required: 'Gender is required' })} tabIndex={0} />
                  <label>Female</label>
                </div>
                <div>
                  <input type="radio" value="O" {...register('gender', { required: 'Gender is required' })} tabIndex={0} />
                  <label>Other</label>
                </div>
              </div>
            </FormField>
          </>
        )}
        {user?.userType === 'C' && (
          <FormField name="description" label="Company description" error={errors.description}>
            <textarea
              placeholder={'Please write here a company description'}
              rows={7}
              className="d-flex flex-col justify-content-center align-items-center "
              {...register('description', { required: 'Company description is required' })}
            />
          </FormField>
        )}

        <FormField name="addressLine1" label="Address" required error={errors.addressLine1}>
          <input type="text" {...register('addressLine1', { required: 'Address is required' })} />
        </FormField>

        <FormField name="city" label="City" required error={errors.city}>
          <input type="text" {...register('city', { required: 'City is required' })} />
        </FormField>

        <FormField name="hasPublicCity">
          <input type="checkbox" {...register('hasPublicCity')} tabIndex={0} />
          <label>Public (city visible in your profile page)</label>
        </FormField>

        <FormField name="state" label="State/ Province/ Region" required error={errors.state}>
          <input type="text" {...register('state', { required: 'State/ Province/ Region is required' })} />
        </FormField>

        <FormField name="zipCode" label="Zip Code" required error={errors.zipCode}>
          <input type="text" {...register('zipCode', { required: 'Zip Code is required' })} />
        </FormField>

        <FormField name="country" label="Your current country" required noErrorBlock>
          <Controller
            name="country"
            control={control}
            rules={{ required: 'Country is required' }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <>
                <CountrySelect value={value} onChange={onChange} />
                <ErrorBlock error={error} />
              </>
            )}
          />
        </FormField>

        <FormField name="hasPublicCountry">
          <input type="checkbox" {...register('hasPublicCountry')} tabIndex={0} />
          <label>Public (country visible in your profile page)</label>
        </FormField>

        <FormField name="nationality" label="Nationality" clarification="Nationality will be kept confidential and will be used internally">
          <Controller
            name="nationality"
            control={control}
            render={({ field: { value, onChange }, fieldState: { error } }) => <CountrySelect value={value} onChange={onChange} />}
          />
        </FormField>

        {user?.userType === 'D' && (
          <>
            <FormField name="contactLanguage" label="Contact language" required error={errors.contactLanguage}>
              <div className="col-12 d-flex justify-content-between align-items-center ">
                <div>
                  <input type="radio" value="E" {...register('contactLanguage', { required: 'Contact language is required' })} tabIndex={0} />
                  <label>English</label>
                </div>
                <div>
                  <input type="radio" value="I" {...register('contactLanguage', { required: 'Contact language is required' })} tabIndex={0} />
                  <label>Italian</label>
                </div>
                <div></div>
                <div></div>
              </div>
            </FormField>

            <FormField
              name="mobileNumber"
              label="Mobile Number"
              clarification="The phone number will be kept confidential and will be used in case of winning for any urgent communications"
              error={errors.mobileNumber}
            >
              <input type="text" {...register('mobileNumber', { validate: containsOnlyNumbers })} />
            </FormField>
          </>
        )}
        <div className="row  d-flex justify-content-center align-items-center mb-7 mt-7">
          <div className="btn-dark_blue col-md-2 mb-sm-3 mb-md-0 rounded mx-5 button" onClick={handleSubmit(gotoProfilePreview)}>
            preview
          </div>
          <button className="btn-success rounded col-md-2 mx-5 button">update</button>
        </div>
      </div>
    </form>
  );
};

export default YourProfile;
